/**
 * src/hooks/useAdmin.js
 * 
 * Custom hook for admin role detection and authorization.
 * Uses Firestore `users/{uid}.role` field for role assignment.
 * Works on Firebase Spark plan
 * 
 * Provides:
 * - isAdmin: Boolean indicating admin status
 * - role: Current user role string
 * - loading: Whether role check is still loading
 * - adminReady: True when auth + role loading is complete
 * 
 * Usage:
 *   const { isAdmin, role, loading } = useAdmin();
 */

import { useState, useEffect } from "react";
import { doc, onSnapshot } from "../lib/firestoreClient";
import { db } from "../lib/firebase";
import { useAuth } from "./useAuth";
import { warnDev } from "../utils/logger";

const ADMIN_ROLES = ["admin", "superadmin"];

function useAdmin() {
  const [role, setRole] = useState(null);
  const [banned, setBanned] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Listen for role & ban changes in Firestore
  useEffect(() => {
    if (!user?.uid) {
      setRole(null);
      setBanned(false);
      setLoading(false);
      return;
    }

    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setRole(data.role || "user");
          setBanned(data.banned === true);
        } else {
          setRole("user");
          setBanned(false);
        }
        setLoading(false);
      },
      (err) => {
        // Permission-denied likely means the user doc doesn't exist
        // or rules block access — treat as regular user
        warnDev("[useAdmin] Firestore listener error:", err.code || err.message);
        setRole("user");
        setBanned(false);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  const isAdmin = ADMIN_ROLES.includes(role);
  const isSuperAdmin = role === "superadmin";
  const adminReady = !loading;

  return {
    user,
    role,
    isAdmin,
    isSuperAdmin,
    banned,
    loading,
    adminReady,
  };
}

export default useAdmin;
export { useAdmin };
